// --------------------------------------------------------------
// Created On: 2024-06-12
// Author: Hannah Vaughan
//
// Last Modified: 2026-01-09
// Modified By: Dimitra Weinstein
//
// Copyright 2024 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  API,
  SEARCH_BAR_BOM_FORMAT_EXAMPLE,
  SEARCH_BAR_PART_FORMAT_EXAMPLE,
  SEARCH_BAR_SERIAL_FORMAT_EXAMPLE,
} from "../../constants/miscellaneous";
import apiRequest from "../../utilities/api/apiRequest";
import getApiError from "../../utilities/api/getApiError";
import styles from "./SearchBar.module.scss";
import Toast from "../Toast/Toast";
import PartModal from "../PartModal/PartModal";

// Search bar that allows the user to search BOM, serial, or part numbers.
export default function SearchBar(props: Props): Component {
  const [searchOption, setSearchOption] = useState<SearchOption>("SERIAL");
  const [inputValue, setInputValue] = useState<string>("");
  const [showPartModal, setShowPartModal] = useState<boolean>(false);
  const [partId, setPartId] = useState<number>(0);
  const [partNumber, setPartNumber] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [unit, setUnit] = useState<string>("");
  const [price, setPrice] = useState<number>(0);
  const [companyDiscount, setCompanyDiscount] = useState<number | null>(null);
  const [weight, setWeight] = useState<number | null>(null);
  const [isImpeller, setIsImpeller] = useState<boolean>(false);
  const [isSellable, setIsSellable] = useState<boolean>(false);
  const [isPaintable, setIsPaintable] = useState<boolean>(false);
  const [hasOtherMaterial, setHasOtherMaterial] = useState<boolean>(false);
  const [orderQuantity, setOrderQuantity] = useState<string>("1");
  const [impellerTrim, setImpellerTrim] = useState<string | null>("0.0");
  const [impellerAngle, setImpellerAngle] = useState<string | null>("0");
  const [selectedPaintColorId, setSelectedPaintColorId] = useState<number | null>(null);
  const [changeMaterialBoxIsChecked, setChangeMaterialBoxIsChecked] = useState<boolean>(false);
  const [replacementMaterialPartNumber, setReplacementMaterialPartNumber] = useState<string | null>(null);

  const navigate = useNavigate();

  const lookupPath = `/lookup/${searchOption.toLowerCase()}/${inputValue.toUpperCase()}`;

  // Perform a search whenever the startSearch flag is set to true.
  useEffect(() => {
    if (props.startSearch || replacementMaterialPartNumber !== null) {
      performSearch();
    }
  }, [props.startSearch, replacementMaterialPartNumber]);

  // If search options are overridden as a prop, then update state whenever the prop changes.
  useEffect(() => {
    if (props.overrideSearchOption !== undefined) {
      setSearchOption(props.overrideSearchOption);
    }
  }, [props.overrideSearchOption]);

  // Returns a placeholder for the search input.
  function getSearchExample(): string {
    switch (searchOption) {
      case "BOM":
        return SEARCH_BAR_BOM_FORMAT_EXAMPLE;
      case "SERIAL":
        return SEARCH_BAR_SERIAL_FORMAT_EXAMPLE;
      case "PART":
        return SEARCH_BAR_PART_FORMAT_EXAMPLE;
      default:
        return "Select a search option from the dropdown menu.";
    }
  }

  // Handle search option updates, by either passing it up if overridden or update state.
  function handleSearchOptionChange(searchOption: SearchOption): void {
    if (props.onOverrideSearchOptionChange !== undefined) {
      props.onOverrideSearchOptionChange(searchOption);
    } else {
      setSearchOption(searchOption);
    }
  }

  // Searches for the BOM, serial, or part number provided in the nav search bar.
  async function performSearch(): Promise<void> {
    const input =
      replacementMaterialPartNumber === null
        ? `${inputValue.toUpperCase()}`
        : `${replacementMaterialPartNumber.toUpperCase()}`;
    const requestBody = {
      type: `${searchOption.toLowerCase()}`,
      input: input,
    };
    props.onLoadingChange(true);
    const [response, responseBody] = await apiRequest(`${API}/search`, "POST", requestBody);
    if (response.ok && responseBody && searchOption === "PART") {
      const [response, responseBody] = (await apiRequest(`${API}/part/${input}`, "GET", null)) as [
        Response,
        ResponseBody
      ];
      if (response.ok && responseBody) {
        props.onErrorMessageChange("");
        setShowPartModal(true);
        setPartId(responseBody.partId);
        setPartNumber(responseBody.partNumber);
        setDescription(responseBody.description);
        setUnit(responseBody.unitName);
        setPrice(responseBody.price);
        setCompanyDiscount(responseBody.companyDiscount);
        setWeight(responseBody.weight);
        setIsPaintable(responseBody.isPaintable);
        setHasOtherMaterial(determinePartHasOtherMaterial(responseBody.partNumber));
        setIsImpeller(responseBody.isImpeller);
        setIsSellable(responseBody.isSellable);
      } else {
        setShowPartModal(false);
        props.onErrorMessageChange(await getApiError(response, "Failed to find part."));
        props.onLoadingChange(false);
      }
      props.onLoadingChange(false);
    } else if (response.ok && responseBody && (searchOption === "BOM" || "SERIAL")) {
      closePartModal();
      navigate(lookupPath);
    } else {
      props.onErrorMessageChange(await getApiError(response, `Requested ${searchOption} number could not be found.`));
      props.onLoadingChange(false);
    }
    setInputValue("");
    props.onSearchComplete();
    props.onLoadingChange(false);

    // Close the navigation drawer if the search was performed from there.
    if (props.isInNavDrawer) {
      props.onClose();
    }
  }

  // Closes the part modal if it was opened due to the search bar.
  function closePartModal(): void {
    setShowPartModal(false);
    setInputValue("");
    setReplacementMaterialPartNumber(null);
    setImpellerAngle("0.0");
    setImpellerTrim("0");
    setOrderQuantity("1");
    setSelectedPaintColorId(null);
    props.onErrorMessageChange("");
    props.onClose();
  }

  // Determines if a part has other material options based on its part number.
  function determinePartHasOtherMaterial(partNumber: string): boolean {
    // This regex checks if a part number contains more than one dash.
    // If there is more than one dash, then the part has other material options.
    const regex = /\W(?:[^\W]+\W){1,}/g;
    if (regex.test(partNumber)) {
      return true;
    } else {
      return false;
    }
  }

  return (
    <Fragment>
      {props.isInDashboard ? (
        <div className="card px-5 py-3">
          <div className="card-title pt-2">
            <h2 style={{ textAlign: "center" }}>Cornell Pump Part Search</h2>
          </div>
          <div className="card-body">
            <div className="row py-2">
              <form data-test="search-bar-form">
                <div className="input-group">
                  <button
                    className="btn btn-secondary dropdown-toggle"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    data-test="search-option-dropdown"
                  >
                    {searchOption}
                  </button>
                  <ul className="dropdown-menu" data-test="search-option-list">
                    <li className="dropdown-item" onClick={() => handleSearchOptionChange("SERIAL")}>
                      SERIAL
                    </li>
                    <li className="dropdown-item" onClick={() => handleSearchOptionChange("BOM")}>
                      BOM
                    </li>
                    <li className="dropdown-item" onClick={() => handleSearchOptionChange("PART")}>
                      PART
                    </li>
                  </ul>
                  <input
                    type="search"
                    className="form-control"
                    placeholder={getSearchExample()}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        performSearch();
                      }
                    }}
                    data-test="search-input-field"
                  />
                  <PartModal
                    showModal={showPartModal}
                    errorMessage={props.errorMessage}
                    partId={partId}
                    partNumber={partNumber}
                    description={description}
                    orderQuantity={orderQuantity}
                    impellerTrim={impellerTrim}
                    impellerAngle={impellerAngle}
                    buildQuantity={"1"}
                    unitName={unit}
                    price={price}
                    companyDiscount={companyDiscount}
                    weight={weight}
                    isImpeller={isImpeller}
                    isSellable={isSellable}
                    isPaintable={isPaintable}
                    hasOtherMaterial={hasOtherMaterial}
                    changeMaterialBoxIsChecked={changeMaterialBoxIsChecked}
                    isAddAllOrderableParts={false}
                    paintColorId={selectedPaintColorId !== null ? selectedPaintColorId : null}
                    parts={[]}
                    newSelectedPart={null}
                    previousPartMaterial={null}
                    onChangeMaterialBoxIsCheckedChange={(partId, checked) =>
                      setChangeMaterialBoxIsChecked(checked ? true : false)
                    }
                    onSelectPartMaterialChange={(part) =>
                      part !== null && setReplacementMaterialPartNumber(part.partNumber)
                    }
                    onQuantityChange={(partId, orderQuantity) => setOrderQuantity(orderQuantity)}
                    onPaintColorChange={(partId, paintColorId) => setSelectedPaintColorId(paintColorId)}
                    onImpellerTrimChange={(partId, impellerTrim) => setImpellerTrim(impellerTrim)}
                    onImpellerAngleChange={(partId, impellerAngle) => setImpellerAngle(impellerAngle)}
                    onClose={() => closePartModal()}
                  />
                </div>
              </form>
            </div>
            <div className="row justify-content-center align-items-center">
              <div className="col-6">
                <div className="row justify-content-center">
                  <button
                    type="button"
                    className={`${styles.searchBarButton} col-6 mt-4`}
                    onClick={(e) => {
                      e.preventDefault();
                      performSearch();
                    }}
                    data-test="search-button"
                  >
                    {searchOption === "BOM" && "Lookup by BOM"}
                    {searchOption === "SERIAL" && "Lookup by serial number"}
                    {searchOption === "PART" && "Lookup by part number"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <form className={`row justify-content-end ${!props.isInNavbar ? "mb-3" : "me-1"}`} data-test="search-bar-form">
          <div className={`${props.isInNavbar ? "col-md-8 col-lg-9" : ""}`}>
            <div className="input-group">
              <button
                className="btn btn-secondary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                data-test="search-option-dropdown"
              >
                {searchOption}
              </button>
              <ul className="dropdown-menu" data-test="search-option-list">
                <li className="dropdown-item" onClick={() => handleSearchOptionChange("SERIAL")}>
                  SERIAL
                </li>
                <li className="dropdown-item" onClick={() => handleSearchOptionChange("BOM")}>
                  BOM
                </li>
                <li className="dropdown-item" onClick={() => handleSearchOptionChange("PART")}>
                  PART
                </li>
              </ul>
              <input
                type="search"
                className="form-control"
                placeholder={getSearchExample()}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    performSearch();
                  }
                }}
                data-test="search-input-field"
              />
              {(props.isInNavbar || props.isInNavDrawer) && (
                <button
                  type="button"
                  className={styles.searchBarButton}
                  onClick={(e) => {
                    e.preventDefault();
                    performSearch();
                  }}
                  data-test="search-button"
                >
                  <i className="fa fa-fw fa-search" />
                </button>
              )}
              <PartModal
                showModal={showPartModal}
                errorMessage={props.errorMessage}
                partId={partId}
                partNumber={partNumber}
                description={description}
                orderQuantity={orderQuantity}
                buildQuantity={"1"}
                impellerTrim={impellerTrim}
                impellerAngle={impellerAngle}
                unitName={unit}
                price={price}
                companyDiscount={companyDiscount}
                weight={weight}
                isImpeller={isImpeller}
                isSellable={isSellable}
                isPaintable={isPaintable}
                hasOtherMaterial={hasOtherMaterial}
                changeMaterialBoxIsChecked={changeMaterialBoxIsChecked}
                isAddAllOrderableParts={false}
                paintColorId={selectedPaintColorId !== null ? selectedPaintColorId : null}
                parts={[]}
                newSelectedPart={null}
                previousPartMaterial={null}
                onChangeMaterialBoxIsCheckedChange={(partId, checked) =>
                  setChangeMaterialBoxIsChecked(checked ? true : false)
                }
                onSelectPartMaterialChange={(part) =>
                  part !== null && setReplacementMaterialPartNumber(part.partNumber)
                }
                onQuantityChange={(partId, orderQuantity) => setOrderQuantity(orderQuantity)}
                onPaintColorChange={(partId, paintColorId) => setSelectedPaintColorId(paintColorId)}
                onImpellerTrimChange={(partId, impellerTrim) => setImpellerTrim(impellerTrim)}
                onImpellerAngleChange={(partId, impellerAngle) => setImpellerAngle(impellerAngle)}
                onClose={() => closePartModal()}
              />
            </div>
          </div>
        </form>
      )}

      {(props.isInNavbar || props.isInDashboard) && props.errorMessage.length > 0 && (
        <Toast
          title="Error"
          message={props.errorMessage}
          show={props.errorMessage.length > 0}
          onClose={() => props.onErrorMessageChange("")}
          type="error"
          data-test="nav-search-bar-error-toast"
        />
      )}
    </Fragment>
  );
}

interface Props {
  isInNavbar: boolean;
  isInNavDrawer: boolean;
  isInDashboard: boolean;
  startSearch: boolean;
  errorMessage: string;
  overrideSearchOption?: SearchOption;
  onSearchComplete: () => void;
  onErrorMessageChange: (errorMessage: string) => void;
  onLoadingChange: (loading: boolean) => void;
  onClose: () => void;
  onOverrideSearchOptionChange?: (searchOption: SearchOption) => void;
}

interface ResponseBody {
  partId: number;
  partNumber: string;
  description: string;
  quantity: number;
  unitName: string;
  price: number;
  companyDiscount: number | null;
  weight: number | null;
  isSellable: boolean;
  isImpeller: boolean;
  isPaintable: boolean;
}

type SearchOption = "BOM" | "SERIAL" | "PART";
